/**
 * Shared plumbing for the native cancel-journey routes (E-H, I) under
 * /settings/cancel: journey state, navigation between steps, the close
 * behaviour (clear the journey, dismiss back to Plan & billing), categorical
 * analytics, and the guards that end a journey whose billing context changed.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useRouter, type Router } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useCancelJourney } from '@/hooks/useCancelJourney';
import { useSubscriptionState } from '@/hooks/useSubscriptionState';
import {
  CANCEL_JOURNEY_ROUTES,
  canStartCancellation,
  cancelJourneyStore,
  formatCancelAccessDate,
  getCancelAccessDate,
  getCancelAnalyticsProperties,
  type CancelJourneyStep,
} from '@/lib/subscription-journey/cancel-journey';
import { trackProductEvent, type ProductAnalyticsProperties } from '@/lib/product-analytics';
import { providerKeyForStore } from './journey-providers';

export const BILLING_ROUTE = '/settings/billing';

/** Header close / "Keep my plan" / "Back to my plan": end the journey. */
export function closeCancelJourney(router: Pick<Router, 'dismissTo'>) {
  cancelJourneyStore.clear();
  router.dismissTo(BILLING_ROUTE as never);
}

/** Steps that stay valid after renewal was turned off (H confirms, I reports). */
const POST_CHANGE_STEPS: ReadonlySet<CancelJourneyStep> = new Set(['provider', 'renewal-off']);

export function useCancelJourneyRoute(step: CancelJourneyStep) {
  const router = useRouter();
  const { t, i18n } = useTranslation('common');
  const journey = useCancelJourney();
  const { snapshot, isLoading } = useSubscriptionState();

  // Stable across snapshot refreshes so "viewed" events fire once per screen.
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const track = useCallback(
    (event: string, properties: Record<string, unknown> = {}) => {
      trackProductEvent(event, {
        ...getCancelAnalyticsProperties({ surface: 'native', snapshot: snapshotRef.current }),
        step,
        ...properties,
      } as ProductAnalyticsProperties);
    },
    [step],
  );

  const goTo = useCallback(
    (next: CancelJourneyStep, options: { replace?: boolean } = {}) => {
      const href = CANCEL_JOURNEY_ROUTES[next] as never;
      if (options.replace) router.replace(href);
      else router.push(href);
    },
    [router],
  );

  const close = useCallback(() => {
    track('subscription_cancel_flow_closed');
    closeCancelJourney(router);
  }, [router, track]);

  // A later step opened without a journey (deep link, JS reload): start at E.
  // Only a step that never saw an active journey redirects, and only while
  // focused: closing the journey clears the store while its steps are still
  // mounted (the focused one included, for the pop animation), and that must
  // not send the stack back to E (seen on iOS, 2026-09-23).
  const isFocused = useIsFocused();
  const sawActiveJourney = useRef(journey.state.active);
  if (journey.state.active) sawActiveJourney.current = true;
  const inactive =
    isFocused &&
    !sawActiveJourney.current &&
    step !== 'reason' &&
    !POST_CHANGE_STEPS.has(step) &&
    !journey.state.active;
  useEffect(() => {
    if (inactive) router.replace(CANCEL_JOURNEY_ROUTES.reason as never);
  }, [inactive, router]);

  // Billing context no longer cancellable (renewal already off elsewhere,
  // plan lapsed, admin grant): end the journey instead of showing stale steps.
  const blocked =
    isFocused &&
    !POST_CHANGE_STEPS.has(step) &&
    !isLoading &&
    Boolean(snapshot) &&
    !canStartCancellation(snapshot);
  useEffect(() => {
    if (!blocked) return;
    closeCancelJourney(router);
  }, [blocked, router]);

  const tier = snapshot?.tier ?? 'free';
  const tierName = tier === 'pro' ? 'Pro' : tier === 'plus' ? 'Plus' : t('subscription.tierFree');
  const provider = t(providerKeyForStore(snapshot?.store));
  const accessDate = useMemo(
    () =>
      formatCancelAccessDate(
        snapshot?.willRenew === false ? snapshot?.renewsAt : getCancelAccessDate(snapshot),
        i18n.language,
      ),
    [i18n.language, snapshot],
  );

  return {
    router,
    journey,
    snapshot,
    tier,
    tierName,
    provider,
    accessDate,
    goTo,
    close,
    track,
    locale: (i18n.language.startsWith('ar') ? 'ar' : 'en') as 'en' | 'ar',
  };
}
